import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import { FLOATING_Z_BASE, type Rect, type SnapRegion, type WorkspacePanelState } from './types';

export interface SnapPreview {
  /** 'tab' = docking into the top bar; otherwise an edge/corner snap. */
  kind: 'tab' | SnapRegion;
  rect: Rect;
}

export interface WorkspaceState {
  panels: Record<string, WorkspacePanelState>;
  /** Ordered ids of the panels currently living in the top tab bar. */
  tabOrder: string[];
  /** The panel the app considers FOCUSED — which may be a floating window. */
  activeId: string | null;
  /**
   * The dock's front TAB, which is a different question from `activeId` and
   * must be tracked separately.
   *
   * Focusing a floating window moves `activeId` to it, but the dock keeps
   * showing whatever tab was in front — so deriving "is this tabbed panel
   * visible?" from `activeId` told the tab behind a floating window that it was
   * hidden. Canvas surfaces (Atlas, Orbits) and the reader paint-gate on that,
   * so the background tab went BLANK the moment anything floated over it.
   */
  activeTabId: string | null;
  maximizedId: string | null;
  /** Monotonic stacking counter for floating panels. */
  zTop: number;
  /** Live drag preview overlay, or null. */
  snapPreview: SnapPreview | null;
  /** Available workspace area in local coords (below the tab bar). */
  workspaceRect: Rect;
}

/**
 * Pure reactive state. All mutations flow through the `workspace` controller,
 * which uses `useWorkspaceStore.setState` / `.getState`. Wrapped in
 * `subscribeWithSelector` so `stores/tabs.ts` (the legacy-shim mirror) can
 * subscribe with a derived-value equality check instead of re-deriving on
 * every raw state change (panel drags/resizes patch `panels` every pointer
 * frame, which would otherwise fan out into every `useTabs` consumer).
 */
export const useWorkspaceStore = create<WorkspaceState>()(
  subscribeWithSelector(
    (): WorkspaceState => ({
      panels: {},
      tabOrder: [],
      activeId: null,
      activeTabId: null,
      maximizedId: null,
      zTop: FLOATING_Z_BASE,
      snapPreview: null,
      workspaceRect: { x: 0, y: 0, width: 0, height: 0 },
    }),
  ),
);
